import { makeScalarToken } from './token.scalar.js';
import { makeGenericToken } from './util.make-generic-token.js';

function makeTransitionToken(propName, input) {
  const propertyKeys = Object.keys(input.properties);
  const tokens = Object.fromEntries(propertyKeys.map(key => [key, {
    easing: makeScalarToken(`${propName}--${key}--easing`, input.properties[key].easing),
    relativeDuration: makeScalarToken(`${propName}--${key}--duration`, input.properties[key].relativeDuration ?? 1),
    relativeDelay: makeScalarToken(`${propName}--${key}--delay`, input.properties[key].relativeDelay ?? 0),
    reducedMotion: {
      addedRelativeDuration: makeScalarToken(`${propName}--${key}--reduce-motion--duration`, input.properties[key].reducedMotion?.addedRelativeDuration ?? 0),
      addedRelativeDelay: makeScalarToken(`${propName}--${key}--reduce-motion--delay`, input.properties[key].reducedMotion?.addedRelativeDelay ?? 0)
    }
  }]));
  return makeGenericToken(function format(options) {
    const durationMs = `${options?.durationMs ?? input.defaultDurationMs}ms`;
    return propertyKeys.flatMap(key => {
      const cssProps = options?.[key] ?? [key];

      // if this property was explicitly disabled, then skip it entirely
      // from the output
      if (cssProps === false) {
        return [];
      }
      const propTokens = tokens[key];

      // ... add every requested property to the output
      return cssProps.map(cssProp => {
        const duration = `calc(${durationMs} * (${propTokens.relativeDuration()} + ${propTokens.reducedMotion.addedRelativeDuration()} * var(--reduce-motion, 0)))`;
        const delay = `calc(${durationMs} * (${propTokens.relativeDelay()} + ${propTokens.reducedMotion.addedRelativeDelay()} * var(--reduce-motion, 0)))`;
        const easing = propTokens.easing();
        const transitionConfig = options?.overrides?.[cssProp]?.(duration, easing, delay) ?? `${duration} ${easing} ${delay}`;
        return `${cssProp} ${transitionConfig}`;
      });
    }).join(",");
  }, function override(nextValue) {
    if (nextValue === undefined) {
      return {};
    }

    // if given a completely new input, then override the created tokens
    // with the newly given inputs
    return propertyKeys.reduce((acc, key) => {
      const nextProp = nextValue[key];
      if (nextProp === undefined) {
        return acc;
      }
      return {
        ...acc,
        ...tokens[key].easing.__override(nextProp.easing),
        ...tokens[key].relativeDuration.__override(nextProp.relativeDuration),
        ...tokens[key].relativeDelay.__override(nextProp.relativeDelay),
        ...tokens[key].reducedMotion.addedRelativeDuration.__override(nextProp.reducedMotion?.addedRelativeDuration),
        ...tokens[key].reducedMotion.addedRelativeDelay.__override(nextProp.reducedMotion?.addedRelativeDelay)
      };
    }, {});
  }, function copy(propName, token) {
    return makeTransitionToken(propName, token);
  }, {
    defaultDurationMs: input.defaultDurationMs,
    properties: tokens
  });
}

export { makeTransitionToken };
//# sourceMappingURL=token.transition.js.map
